// src/components/Navbar.jsx
import * as React from "react";
import { Link } from "react-router-dom";
import {
  Menubar,
  MenubarMenu,
  MenubarTrigger,
} from "../components/ui/menubar";
import { Button } from "../components/ui/button";
import { Sheet, SheetContent, SheetClose, SheetTrigger } from "../components/ui/sheet";
import { Separator } from "../components/ui/separator";
import {
  Menu,
  LogOut,
  User,
  Package,
  LayoutGrid,
  Sun,
  Moon,
  Home,
  Phone,
  BookOpen,
} from "lucide-react";
import { Switch } from "../components/ui/switch";
import { cn } from "../lib/utils";
import { useAuth } from "../contexts/AuthContext";

export default function Navbar({ toggleDarkMode, darkMode }) {
  const {
    user,
    userLoading,
    isAuthLoading,
    handleLogout,
    openLogin,
    openRegister
  } = useAuth();

  return (
    <nav className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      {/* Fixed width container that matches viewport */}
      <div className="mx-auto w-full max-w-screen-2xl px-4 md:px-6 lg:px-8">
        <div className="flex h-14 items-center justify-between">
          
          {/* Left Section - Logo + Dark Mode */}
          <div className="flex items-center gap-3 md:gap-4">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3">
              <div className="flex items-baseline bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                <span className="text-2xl md:text-3xl font-extrabold tracking-tight">SE</span>
                <span className="text-xl md:text-2xl font-bold tracking-tight">RRA</span>
                <span className="text-2xl md:text-3xl font-extrabold tracking-tight">ND</span>
              </div>
              <span className="text-sm md:text-base font-medium text-muted-foreground hidden lg:inline">
                | Plug Wa Kazi
              </span>
            </Link>

            {/* Dark Mode Switch */}
            <div className="relative inline-flex items-center scale-90 md:scale-100">
              <Switch
                checked={darkMode}
                onCheckedChange={toggleDarkMode}
                aria-label="Toggle dark mode"
                className="data-[state=checked]:bg-primary"
              />
              <span
                className={cn(
                  "absolute left-0.5 top-1/2 -translate-y-1/2 text-xs transition-all pointer-events-none",
                  darkMode ? "translate-x-5" : "translate-x-0"
                )}
              >
                {darkMode ? <Moon className="h-3.5 w-3.5" /> : <Sun className="h-3.5 w-3.5" />}
              </span>
            </div>
          </div>

          {/* Desktop Menu - Dramatically tighter spacing and smaller size */}
          <div className="hidden md:flex md:items-center md:gap-3 lg:gap-4">
            <Menubar className="border-none bg-transparent p-0">
              <MenubarMenu>
                <MenubarTrigger className="hover:bg-accent hover:text-accent-foreground px-2 py-1.5 rounded-md text-sm">
                  <Link to="/" className="flex items-center gap-1">
                    <Home className="h-3.5 w-3.5" />
                    Home
                  </Link>
                </MenubarTrigger>
              </MenubarMenu>

              <MenubarMenu>
                <MenubarTrigger className="hover:bg-accent hover:text-accent-foreground px-2 py-1.5 rounded-md text-sm">
                  <Link to="/categories" className="flex items-center gap-1">
                    <LayoutGrid className="h-3.5 w-3.5" />
                    Categories
                  </Link>
                </MenubarTrigger>
              </MenubarMenu>

              <MenubarMenu>
                <MenubarTrigger className="hover:bg-accent hover:text-accent-foreground px-2 py-1.5 rounded-md text-sm">
                  <Link to="/blog" className="flex items-center gap-1">
                    <BookOpen className="h-3.5 w-3.5" />
                    Blog
                  </Link>
                </MenubarTrigger>
              </MenubarMenu>

              {user?.username && (
                <>
                  <MenubarMenu>
                    <MenubarTrigger className="hover:bg-accent hover:text-accent-foreground px-2 py-1.5 rounded-md text-sm">
                      <Link to="/profile" className="flex items-center gap-1">
                        <User className="h-3.5 w-3.5" />
                        Profile
                      </Link>
                    </MenubarTrigger>
                  </MenubarMenu>

                  <MenubarMenu>
                    <MenubarTrigger className="hover:bg-accent hover:text-accent-foreground px-2 py-1.5 rounded-md text-sm">
                      <Link to="/manage-orders" className="flex items-center gap-1">
                        <Package className="h-3.5 w-3.5" />
                        Orders
                      </Link>
                    </MenubarTrigger>
                  </MenubarMenu>
                </>
              )}

              <MenubarMenu>
                <MenubarTrigger className="hover:bg-accent hover:text-accent-foreground px-2 py-1.5 rounded-md text-sm">
                  <Link to="/contact" className="flex items-center gap-1">
                    <Phone className="h-3.5 w-3.5" />
                    Contact
                  </Link>
                </MenubarTrigger>
              </MenubarMenu>
            </Menubar>

            {/* Auth Section */}
            {userLoading || isAuthLoading ? (
              <div className="flex items-center space-x-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            ) : user?.username ? (
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium hidden xl:inline">
                  Jambo, <span className="text-foreground">{user.username}</span>
                </span>
                <Button variant="ghost" size="sm" onClick={handleLogout}>
                  <LogOut className="mr-1 h-4 w-4" />
                  Logout
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={openLogin}>
                  Login
                </Button>
                <Button variant="default" size="sm" onClick={openRegister}>
                  Register
                </Button>
              </div>
            )}
          </div>

          {/* Mobile Menu - Unchanged */}
          <div className="md:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[380px]">
                <div className="flex flex-col gap-6 py-8">
                  <SheetClose asChild>
                    <Link
                      to="/"
                      className="flex items-center gap-3 text-lg font-medium hover:text-primary transition-colors"
                    >
                      <Home className="h-5 w-5" />
                      Home
                    </Link>
                  </SheetClose>

                  <SheetClose asChild>
                    <Link
                      to="/categories"
                      className="flex items-center gap-3 text-lg font-medium hover:text-primary transition-colors"
                    >
                      <LayoutGrid className="h-5 w-5" />
                      Categories
                    </Link>
                  </SheetClose>

                  <SheetClose asChild>
                    <Link
                      to="/blog"
                      className="flex items-center gap-3 text-lg font-medium hover:text-primary transition-colors"
                    >
                      <BookOpen className="h-5 w-5" />
                      Blog
                    </Link>
                  </SheetClose>

                  {user?.username && (
                    <>
                      <SheetClose asChild>
                        <Link
                          to="/profile"
                          className="flex items-center gap-3 text-lg font-medium hover:text-primary transition-colors"
                        >
                          <User className="h-5 w-5" />
                          Profile
                        </Link>
                      </SheetClose>

                      <SheetClose asChild>
                        <Link
                          to="/manage-orders"
                          className="flex items-center gap-3 text-lg font-medium hover:text-primary transition-colors"
                        >
                          <Package className="h-5 w-5" />
                          Manage Orders
                        </Link>
                      </SheetClose>
                    </>
                  )}

                  <SheetClose asChild>
                    <Link
                      to="/contact"
                      className="flex items-center gap-3 text-lg font-medium hover:text-primary transition-colors"
                    >
                      <Phone className="h-5 w-5" />
                      Contact
                    </Link>
                  </SheetClose>

                  <Separator className="my-4" />

                  {user?.username ? (
                    <Button
                      variant="destructive"
                      onClick={handleLogout}
                      className="w-full justify-start gap-3"
                    >
                      <LogOut className="h-5 w-5" />
                      Logout
                    </Button>
                  ) : (
                    <div className="flex flex-col gap-3">
                      <Button
                        variant="outline"
                        onClick={openLogin}
                        asChild
                        className="w-full justify-start"
                      >
                        <SheetClose>Login</SheetClose>
                      </Button>
                      <Button
                        variant="default"
                        onClick={openRegister}
                        asChild
                        className="w-full justify-start"
                      >
                        <SheetClose>Register</SheetClose>
                      </Button>
                    </div>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
}
